import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { matchesAPI } from '../services/api';

function ErrorStatisticsPanel({ matchId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchErrorStats();
  }, [matchId]);

  const fetchErrorStats = async () => {
    try {
      setLoading(true);
      const statsData = await matchesAPI.getErrorStats(matchId);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to fetch error stats:', error);
      toast.error('Failed to load error statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading statistics...</div>;
  }

  if (!stats) {
    return <div>No statistics available</div>;
  }

  const getConfidenceLevel = (value) => {
    if (value >= 0.7) return 'High';
    if (value >= 0.4) return 'Medium';
    return 'Low';
  };

  return (
    <div className="card">
      <h3 style={{ marginBottom: '1.5rem' }}>Error Statistics & Analysis</h3>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '4px', borderLeft: '4px solid #3b82f6' }}>
          <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Total Errors</div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e3a8a' }}>{stats.total_errors}</div>
        </div>

        <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '4px', borderLeft: '4px solid #10b981' }}>
          <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Verified</div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#065f46' }}>{stats.verified_errors}</div>
        </div>

        <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '4px', borderLeft: '4px solid #ef4444' }}>
          <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Unverified</div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#7f1d1d' }}>{stats.unverified_errors}</div>
        </div>

        <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '4px', borderLeft: '4px solid #f59e0b' }}>
          <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Avg Confidence</div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#92400e' }}>
            {(stats.average_confidence * 100).toFixed(0)}%
          </div>
        </div>
      </div>

      {/* Errors by Type */}
      {stats.errors_by_type && Object.keys(stats.errors_by_type).length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ marginBottom: '1rem', color: '#1f2937' }}>Errors by Type</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem' }}>
            {Object.entries(stats.errors_by_type).map(([type, count]) => (
              <div
                key={type}
                style={{
                  padding: '1rem',
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '0.875rem', fontWeight: '500', textTransform: 'capitalize', marginBottom: '0.5rem' }}>
                  {type}
                </div>
                <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#3b82f6' }}>{count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confidence Distribution */}
      <div style={{ marginBottom: '2rem' }}>
        <h4 style={{ marginBottom: '1rem', color: '#1f2937' }}>Confidence Distribution</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
          {/* High Confidence */}
          <div style={{ padding: '1rem', background: '#ecfdf5', border: '1px solid #10b981', borderRadius: '4px' }}>
            <div style={{ fontSize: '0.875rem', color: '#047857', marginBottom: '0.5rem', fontWeight: '500' }}>
              ✓ High Confidence
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#059669', marginBottom: '0.5rem' }}>
              {stats.confidence_distribution.high}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>≥ 70% confidence</div>
            {stats.total_errors > 0 && (
              <div style={{ fontSize: '0.875rem', color: '#047857', marginTop: '0.5rem' }}>
                {((stats.confidence_distribution.high / stats.total_errors) * 100).toFixed(0)}% of total
              </div>
            )}
          </div>

          {/* Medium Confidence */}
          <div style={{ padding: '1rem', background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '4px' }}>
            <div style={{ fontSize: '0.875rem', color: '#b45309', marginBottom: '0.5rem', fontWeight: '500' }}>
              ⚠ Medium Confidence
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#d97706', marginBottom: '0.5rem' }}>
              {stats.confidence_distribution.medium}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>40% - 70% confidence</div>
            {stats.total_errors > 0 && (
              <div style={{ fontSize: '0.875rem', color: '#b45309', marginTop: '0.5rem' }}>
                {((stats.confidence_distribution.medium / stats.total_errors) * 100).toFixed(0)}% of total
              </div>
            )}
          </div>

          {/* Low Confidence */}
          <div style={{ padding: '1rem', background: '#fef2f2', border: '1px solid #ef4444', borderRadius: '4px' }}>
            <div style={{ fontSize: '0.875rem', color: '#991b1b', marginBottom: '0.5rem', fontWeight: '500' }}>
              ✗ Low Confidence
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#dc2626', marginBottom: '0.5rem' }}>
              {stats.confidence_distribution.low}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>&lt; 40% confidence</div>
            {stats.total_errors > 0 && (
              <div style={{ fontSize: '0.875rem', color: '#991b1b', marginTop: '0.5rem' }}>
                {((stats.confidence_distribution.low / stats.total_errors) * 100).toFixed(0)}% of total
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Help Text */}
      <div style={{ padding: '1rem', background: '#eff6ff', borderRadius: '4px' }}>
        <h5 style={{ marginBottom: '0.5rem' }}>About Error Detection</h5>
        <div style={{ fontSize: '0.875rem', lineHeight: '1.6', color: '#1e40af' }}>
          <div>• <strong>High Confidence:</strong> AI is highly certain about this error detection</div>
          <div>• <strong>Medium Confidence:</strong> AI detected an error but recommends manual verification</div>
          <div>• <strong>Low Confidence:</strong> Uncertain detection - likely requires manual review or correction</div>
          <div style={{ marginTop: '0.5rem' }}>Review and verify errors to improve coaching analysis and train future models.</div>
        </div>
      </div>
    </div>
  );
}

export default ErrorStatisticsPanel;
